import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { api } from "../api/client";

type AuthState = {
  authenticated: boolean | null; // null = still checking
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get<{ authenticated: boolean }>("/api/auth/me");
      setAuthenticated(!!r.authenticated);
    } catch {
      setAuthenticated(false);
    }
  }, []);

  const login = useCallback(
    async (password: string) => {
      await api.post("/api/auth/login", { password });
      await refresh();
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    await api.post("/api/auth/logout");
    setAuthenticated(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <AuthCtx.Provider value={{ authenticated, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
